"use client";

import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { readMobileActions } from "@/lib/mobile/offline-queue";
import {
  didReleaseChange,
  hasDirtyRefreshForm,
  isReleaseComparable,
} from "@/lib/release/refresh-safety";

const checkIntervalMs = 60_000;
const retrySafeRefreshMs = 5_000;
const releaseChannelName = "threadops-release-v1";

type VersionPayload = { release?: unknown };

export function ReleaseGuard({ loadedRelease }: { loadedRelease: string }) {
  const [availableRelease, setAvailableRelease] = useState<string | null>(null);
  const refreshStarted = useRef(false);
  const serviceWorkerUpdatedFor = useRef<string | null>(null);

  const updateServiceWorker = useCallback(async (release: string) => {
    if (serviceWorkerUpdatedFor.current === release) return;
    serviceWorkerUpdatedFor.current = release;
    if (!("serviceWorker" in navigator)) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration("/");
      await registration?.update();
      registration?.waiting?.postMessage({ type: "SKIP_WAITING" });
    } catch {
      // A hard reload still obtains the content-hashed application assets.
    }
  }, []);

  const hasUnsafeLocalWork = useCallback(async () => {
    const refreshEvent = new CustomEvent("threadops:before-release-refresh", {
      cancelable: true,
    });
    if (!window.dispatchEvent(refreshEvent)) return true;
    if (hasDirtyRefreshForm(document.forms)) return true;
    if (!("indexedDB" in window)) return false;
    try {
      return (await readMobileActions(1)).length > 0;
    } catch {
      return false;
    }
  }, []);

  const refreshWhenSafe = useCallback(async (release: string, force = false) => {
    if (refreshStarted.current) return;
    setAvailableRelease(release);
    await updateServiceWorker(release);
    if (!force && await hasUnsafeLocalWork()) return;
    refreshStarted.current = true;
    window.location.reload();
  }, [hasUnsafeLocalWork, updateServiceWorker]);

  useEffect(() => {
    if (!isReleaseComparable(loadedRelease)) return;
    let stopped = false;
    let checking = false;
    let lastBroadcastRelease: string | null = null;
    const channel = "BroadcastChannel" in window
      ? new BroadcastChannel(releaseChannelName)
      : null;

    const observeRelease = (release: string, broadcast: boolean) => {
      if (stopped || !didReleaseChange(loadedRelease, release)) return;
      if (broadcast && lastBroadcastRelease !== release) {
        lastBroadcastRelease = release;
        channel?.postMessage({ release });
      }
      void refreshWhenSafe(release);
    };

    const check = async () => {
      if (checking || stopped || !navigator.onLine) return;
      checking = true;
      try {
        const response = await fetch(`/api/version?loaded=${encodeURIComponent(loadedRelease)}`, {
          cache: "no-store",
          headers: { "cache-control": "no-cache" },
        });
        if (!response.ok) return;
        const payload = await response.json() as VersionPayload;
        if (typeof payload.release === "string") observeRelease(payload.release, true);
      } catch {
        // Offline and transient deployment failures are retried on the next signal.
      } finally {
        checking = false;
      }
    };

    const checkVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    const receive = (event: MessageEvent<VersionPayload>) => {
      if (typeof event.data?.release === "string") observeRelease(event.data.release, false);
    };
    channel?.addEventListener("message", receive);
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", checkVisible);
    const interval = window.setInterval(check, checkIntervalMs);
    void check();

    return () => {
      stopped = true;
      window.clearInterval(interval);
      channel?.removeEventListener("message", receive);
      channel?.close();
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", checkVisible);
    };
  }, [loadedRelease, refreshWhenSafe]);

  useEffect(() => {
    if (!availableRelease) return;
    const retry = window.setInterval(
      () => { void refreshWhenSafe(availableRelease); },
      retrySafeRefreshMs,
    );
    return () => window.clearInterval(retry);
  }, [availableRelease, refreshWhenSafe]);

  if (!availableRelease) return null;
  return (
    <aside
      aria-live="assertive"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto flex max-w-3xl flex-col gap-3 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] p-4 shadow-xl sm:flex-row sm:items-center sm:justify-between"
      role="status"
    >
      <div>
        <p className="font-semibold">A new ThreadOps version is ready</p>
        <p className="mt-1 text-sm text-[var(--color-foreground-muted)]">
          Finish or save current edits and queued offline work. This page will refresh automatically when it is safe.
        </p>
      </div>
      <Button className="shrink-0" onClick={() => void refreshWhenSafe(availableRelease, true)}>
        <RefreshCw aria-hidden="true" className="size-4" />
        Refresh now
      </Button>
    </aside>
  );
}
